import { useEffect, useMemo, useState } from "react";
import { useMoveNote, useSpaceTree, useSpaces } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { MoveResult, Note, NoteTreeNode } from "@/api/types";
import { useToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TreePicker } from "./TreePicker";

/** Notes under `id` in a tree, any depth. */
export function countDescendants(tree: NoteTreeNode[] | undefined, id: string): number {
  const find = (nodes: NoteTreeNode[]): NoteTreeNode | undefined => {
    for (const n of nodes) {
      if (n.id === id) return n;
      const hit = find(n.children);
      if (hit) return hit;
    }
    return undefined;
  };
  const size = (n: NoteTreeNode): number => n.children.reduce((sum, c) => sum + 1 + size(c), 0);
  const node = tree ? find(tree) : undefined;
  return node ? size(node) : 0;
}

export function MoveNoteDialog({ note, open, onOpenChange }: { note: Note; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data: spaces } = useSpaces();
  const [spaceId, setSpaceId] = useState<string>(note.spaceId);
  const [parentId, setParentId] = useState<string | null>(note.parentId ?? null);
  const targetTree = useSpaceTree(spaceId, open);
  const homeTree = useSpaceTree(note.spaceId, open);
  const move = useMoveNote(note.id);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setSpaceId(note.spaceId);
      setParentId(note.parentId ?? null);
    }
  }, [open, note.spaceId, note.parentId]);

  // Only spaces the user can write to; a read-only target is refused by the API anyway.
  const targets = useMemo(() => (spaces ?? []).filter((s) => s.role !== "viewer"), [spaces]);
  const crossSpace = spaceId !== note.spaceId;
  const unchanged = !crossSpace && parentId === (note.parentId ?? null);
  const descendants = countDescendants(homeTree.data, note.id);

  function report(result: MoveResult) {
    const notes: string[] = [];
    for (const c of result.collisions) notes.push(`A page named “${c.title}” already exists there.`);
    notes.push(...result.warnings);
    const where = result.note.breadcrumbs?.length ? result.note.breadcrumbs.map((b) => b.title).join(" / ") : "top level";
    toast({
      title: notes.length ? "Moved, with notes" : "Note moved",
      description: notes.length ? notes.join(" ") : `Now under ${where}.`,
      tone: notes.length ? "default" : "success",
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move “{note.title || "Untitled"}”</DialogTitle>
          <DialogDescription>
            Pick a space and a parent page, or top level.
            {descendants > 0 && ` ${descendants} nested ${descendants === 1 ? "page moves" : "pages move"} with it.`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="move-space">Space</Label>
            <select
              id="move-space"
              className="h-9 w-full rounded-lg border border-input bg-card px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30"
              value={spaceId}
              onChange={(e) => {
                const next = e.target.value;
                setSpaceId(next);
                // Back home: the current parent is a sensible default; elsewhere: top level.
                setParentId(next === note.spaceId ? (note.parentId ?? null) : null);
              }}
            >
              {targets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.id === note.spaceId ? " (current)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label id="move-parent-label">Parent page</Label>
            <div aria-labelledby="move-parent-label">
              <TreePicker tree={targetTree.data} loading={targetTree.isLoading} value={parentId} onChange={setParentId} excludeId={crossSpace ? undefined : note.id} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={unchanged || move.isPending}
            onClick={() =>
              move.mutate(
                { parentId, spaceId: crossSpace ? spaceId : undefined },
                {
                  onSuccess: (result) => {
                    onOpenChange(false);
                    report(result);
                  },
                  onError: (err) => toast({ title: "Could not move note", description: errorMessage(err), tone: "error" }),
                },
              )
            }
          >
            {move.isPending ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
